import React from 'react';
import withShipment from '../../withShipment';
import CustomToolbar from '../global/CustomToolbar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import { userInfoSelector } from '../../redux/selectors/auth';
import { weeklyReportAuditLogsSelector } from '../../redux/selectors/audit';
import { listWeeklyReportAudit } from '../../redux/actions/audit';
import { Box, Button, Collapse, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';

const TZ = 'America/Chicago';

// ── Role → look (mirrors Manage Employee role tiers) ───────────────────────
const ROLE_STYLE = {
  owner: { color: '#0d9488', soft: '#ccfbf1', grad: '#ccfbf1' },
  manager: { color: '#6d28d9', soft: '#f1ebff', grad: '#f1ebff' },
  reader: { color: '#2563eb', soft: '#e8efff', grad: '#eef3ff' },
  supervisor: { color: '#0ea5e9', soft: '#e0f2fe', grad: '#e0f2fe' },
  techadmin: { color: '#b45309', soft: '#fff4e0', grad: '#fff4e0' },
  employee: { color: '#475569', soft: '#eef2f9', grad: '#f1f5f9' },
};
const roleStyle = (k) => ROLE_STYLE[k] || ROLE_STYLE.employee;

const TYPE_BADGE = {
  add: { label: 'Added', color: '#0a8a4a', bg: '#e7f6ee' },
  edit: { label: 'Modified', color: '#b45309', bg: '#fff4e0' },
  del: { label: 'Deleted', color: '#c0392b', bg: '#fdecea' },
};

const ACTION_LABEL = {
  MANAGER_BANK_ADD: 'Manager Bank Entry',
  MANAGER_SUMMARY_UPDATE: 'Manager Summary',
  MANAGER_SETTING_UPDATE: 'Manager Setting',
  REPORT_FIELD_UPDATE: 'Report Field',
  TRANSACTION_MODIFIED: 'Transaction',
  CASH_LEFT_MODIFIED: 'Cash Left',
};

const FIELD_LABELS = {
  jpOtherIn: 'JP Other In',
  jpOtherOut: 'JP Other Out',
  endingBalance: 'Ending Balance',
  openingBalance: 'Opening Balance',
  morningPull: 'Morning Pull',
  nightPull: 'Night Pull',
  addBank: 'Add Bank',
  loanIn: 'Loan In',
  employeeShortPay: 'Employee Short Pay',
  minusBank: 'Minus Bank',
  jackpotNotPay: 'Jackpot Not Pay',
  bank1: 'Bank 1',
  bank2: 'Bank 2',
  bank3: 'Bank 3',
  manager: 'Manager',
  atm: 'ATM',
  zelle: 'Zelle',
  supervisor: 'Supervisor',
  minusRollover: 'Minus Rollover',
  lastWeekProfit: 'Last Week Profit',
  expenseSet: 'Expenses',
  managerName: 'Manager Name',
  profitThreshold: 'Profit Threshold',
  managerPercentage: 'Manager %',
  amount: 'Amount',
  type: 'Type',
  userName: 'Entered By',
  employeeName: 'Employee',
  createdDate: 'Created',
  delivery: 'Delivery',
  adjustments: 'Adjustments',
  otherItems: 'Other Items',
  atmTransactions: 'ATM Transactions',
  weeklyNetProfit: 'Weekly Net Profit',
  remainingProfit: 'Remaining Profit',
  isWeeklyNetProfitEdited: 'Net Profit Edited',
  totalATMCash: 'Total ATM Cash',
  cashLeft: 'Cash Left',
  description: 'Description',
};
const fieldLabel = (f) => FIELD_LABELS[f] || f;

// ── Value helpers ──────────────────────────────────────────────────────────
const toNumber = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v) || 0;
  if (typeof v === 'object' && v.$numberDecimal !== undefined)
    return Number(v.$numberDecimal) || 0;
  return 0;
};
const formatMoney = (n) => {
  const num = toNumber(n);
  const str = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `-$${str}` : `$${str}`;
};
const isIso = (v) =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v);

const isEmptyVal = (v) => {
  if (v === null || v === undefined || v === '') return true;
  if (typeof v === 'object' && v.$numberDecimal !== undefined) return false;
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

const fullDate = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    return `${date.toLocaleDateString('en-US', {
      timeZone: TZ,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })} · ${date.toLocaleTimeString('en-US', {
      timeZone: TZ,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`;
  } catch (e) {
    return String(d);
  }
};
const timeOnly = (d) => {
  try {
    return new Date(d).toLocaleTimeString('en-US', {
      timeZone: TZ,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch (e) {
    return '';
  }
};

// Day grouping in Chicago time. en-CA → YYYY-MM-DD (stable, sortable).
// The gameroom business day runs past midnight, so edits land on the calendar
// day AFTER the report they belong to (e.g. a report for Monday is read/edited
// early Tuesday). We attribute each change to the prior calendar day so the
// strip reads "Monday" instead of "Tuesday". Both grouping and the "today"
// reference (scopeLabel) flow through here, so the shift stays consistent.
const dayKeyOf = (d) => {
  try {
    return new Date(new Date(d).getTime() - 86400000).toLocaleDateString(
      'en-CA',
      { timeZone: TZ }
    );
  } catch (e) {
    return 'unknown';
  }
};
const parseDayKey = (key) => {
  const [y, m, dd] = key.split('-').map(Number);
  return new Date(y, m - 1, dd, 12, 0, 0);
};
const scopeLabel = (key) => {
  const today = dayKeyOf(new Date());
  if (key === today) return 'Today';
  const diff = Math.round((parseDayKey(today) - parseDayKey(key)) / 86400000);
  if (diff === 1) return 'Yesterday';
  if (diff > 1) return `${diff} days ago`;
  if (diff === -1) return 'Tomorrow';
  return '';
};

// Render a scalar/complex value as JSX (money, dates, lists, objects).
const renderValue = (v) => {
  if (isEmptyVal(v)) return <em style={{ color: '#9aa5b8' }}>(none)</em>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (v instanceof Date) return fullDate(v);
  if (isIso(v)) return fullDate(v);
  if (typeof v === 'number') return formatMoney(v);
  if (typeof v === 'string') {
    const t = v.trim();
    if (/^-?\d+(\.\d+)?$/.test(t)) return formatMoney(Number(t));
    return v;
  }
  if (typeof v !== 'object') return String(v);
  if (v.$numberDecimal !== undefined)
    return formatMoney(Number(v.$numberDecimal));
  if (v.$date !== undefined) return fullDate(v.$date);
  if (Array.isArray(v)) return renderList(v);
  return renderObject(v);
};

const entryInline = (item) => {
  if (item == null) return <em>(none)</em>;
  if (typeof item !== 'object') return renderValue(item);
  if (item.$numberDecimal !== undefined)
    return formatMoney(Number(item.$numberDecimal));
  const label = item.description || item.type || item.name || '(item)';
  const hasAmount =
    item.amount !== undefined && item.amount !== null && item.amount !== '';
  const subParts = [];
  if (item.comments) subParts.push(item.comments);
  if (item.employeeName) subParts.push(`Employee: ${item.employeeName}`);
  if (item.userName) subParts.push(`By: ${item.userName}`);
  return (
    <span>
      <strong>{label}</strong>
      {hasAmount && (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {' '}
          — {formatMoney(item.amount)}
        </span>
      )}
      {subParts.length > 0 && (
        <div style={{ fontSize: '0.85em', color: '#6b7896', marginLeft: 4 }}>
          {subParts.join(' · ')}
        </div>
      )}
    </span>
  );
};
const renderList = (arr) => {
  if (!arr || arr.length === 0)
    return <em style={{ color: '#9aa5b8' }}>(empty)</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {arr.map((it, i) => (
        <li key={i} style={{ marginBottom: 3 }}>
          {entryInline(it)}
        </li>
      ))}
    </ul>
  );
};
const renderObject = (obj) => {
  const keys = Object.keys(obj).filter((k) => !isEmptyVal(obj[k]));
  if (keys.length === 0) return <em style={{ color: '#9aa5b8' }}>(empty)</em>;
  return (
    <div>
      {keys.map((k) => (
        <div key={k} style={{ display: 'flex', gap: 6, fontSize: '0.92em' }}>
          <span style={{ color: '#6b7896', minWidth: 96 }}>
            {fieldLabel(k)}:
          </span>
          <span>{renderValue(obj[k])}</span>
        </div>
      ))}
    </div>
  );
};

// ── List-field (expense set etc.) entry diffing ────────────────────────────
// Fields like expenseSet hold an array of entry objects plus a computed
// "Total" row. When one entry is added/removed/edited the audit must show
// ONLY that entry — not the whole before/after list. Entries carry no stable
// id, so we match on a composite identity (label + employee + type + manager
// flag) and compare amounts, while ignoring the auto-computed Total row.
const TOTAL_RE = /^\s*total\s*$/i;
const entryLabel = (e) =>
  (e && (e.description || e.name || e.type)) || '(item)';
const entryEmp = (e) => (e && (e.employeeName || null)) || null;

const looksLikeEntryArray = (v) =>
  Array.isArray(v) &&
  v.some((x) => x && typeof x === 'object' && x.$numberDecimal === undefined);

const isTotalRow = (e) =>
  TOTAL_RE.test(String((e && (e.description || e.name)) || '').trim());

const entryIdentity = (e) =>
  [
    String((e && (e.description || e.name || e.type)) || '')
      .trim()
      .toLowerCase(),
    String((e && e.employeeName) || '')
      .trim()
      .toLowerCase(),
    String((e && e.expenseType) || '')
      .trim()
      .toLowerCase(),
    e && e.isManager ? '1' : '0',
  ].join('|');

const diffEntryArrays = (oldArr, newArr) => {
  const groupOf = (arr) => {
    const m = new Map();
    (Array.isArray(arr) ? arr : []).forEach((e) => {
      if (!e || typeof e !== 'object' || isTotalRow(e)) return;
      const k = entryIdentity(e);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  };
  const om = groupOf(oldArr);
  const nm = groupOf(newArr);
  const keys = new Set([...om.keys(), ...nm.keys()]);
  const added = [];
  const removed = [];
  const modified = [];
  keys.forEach((k) => {
    const ol = om.get(k) || [];
    const nl = nm.get(k) || [];
    const min = Math.min(ol.length, nl.length);
    for (let i = 0; i < min; i++) {
      if (toNumber(ol[i].amount) !== toNumber(nl[i].amount)) {
        modified.push({ old: ol[i], new: nl[i] });
      }
    }
    for (let i = min; i < nl.length; i++) added.push(nl[i]);
    for (let i = min; i < ol.length; i++) removed.push(ol[i]);
  });
  return { added, removed, modified };
};

// Expand one audit log into per-field change "lines" (or one whole-action line).
const buildLines = (log) => {
  const fields = log.changedFields || [];
  const target =
    log.targetClockId && log.targetUserId
      ? {
          userId: log.targetUserId,
          userName: log.targetUserName,
          clockId: log.targetClockId,
          locationId: log.locationId,
        }
      : null;
  const base = {
    log,
    time: log.changedAt,
    session: log.readingSessionId,
    target,
    emp: log.targetUserName || null,
  };

  // Shift transaction add / edit / delete → one concise line that reads like
  // the entry it represents: "Food — Added — + $50.00" (by whoever did it).
  if (/^transaction/i.test(log.entityType || '')) {
    const oldV = log.oldValues || {};
    const newV = log.newValues || {};
    const ref = Object.keys(newV).length ? newV : oldV;
    const label =
      ref.description ||
      ref.nameInput ||
      ref.expenseType ||
      ref.type ||
      'Transaction';
    const emptyOld = Object.keys(oldV).length === 0;
    const emptyNew = Object.keys(newV).length === 0;
    let type = 'edit';
    if (log.action === 'TRANSACTION_ADDED' || (emptyOld && !emptyNew))
      type = 'add';
    else if (log.action === 'TRANSACTION_REMOVED' || (!emptyOld && emptyNew))
      type = 'del';
    return [
      {
        ...base,
        key: `${log.auditId}:tx`,
        type,
        isEntry: true,
        field: 'transaction',
        fieldLabel: label,
        emp: log.targetUserName || base.emp,
        oldVal: oldV.amount,
        newVal: newV.amount,
      },
    ];
  }

  if (!fields.length) {
    const type = log.action === 'MANAGER_BANK_ADD' ? 'add' : 'edit';
    return [
      {
        ...base,
        key: `${log.auditId}:whole`,
        type,
        isWhole: true,
        fieldLabel: ACTION_LABEL[log.action] || log.action,
        wholeValues: log.newValues || log.oldValues,
      },
    ];
  }

  const out = [];
  fields.forEach((f) => {
    const o = log.oldValues ? log.oldValues[f] : undefined;
    const n = log.newValues ? log.newValues[f] : undefined;

    // List of entry objects (expenses, other items, ATM rows) — diff entries
    // and emit one line per actual add / remove / amount change.
    if (looksLikeEntryArray(o) || looksLikeEntryArray(n)) {
      const { added, removed, modified } = diffEntryArrays(o, n);
      added.forEach((e, i) =>
        out.push({
          ...base,
          key: `${log.auditId}:${f}:add:${i}`,
          type: 'add',
          isEntry: true,
          field: f,
          fieldLabel: entryLabel(e),
          emp: entryEmp(e) || base.emp,
          newVal: e.amount,
        })
      );
      removed.forEach((e, i) =>
        out.push({
          ...base,
          key: `${log.auditId}:${f}:del:${i}`,
          type: 'del',
          isEntry: true,
          field: f,
          fieldLabel: entryLabel(e),
          emp: entryEmp(e) || base.emp,
          oldVal: e.amount,
        })
      );
      modified.forEach((pair, i) =>
        out.push({
          ...base,
          key: `${log.auditId}:${f}:mod:${i}`,
          type: 'edit',
          isEntry: true,
          field: f,
          fieldLabel: entryLabel(pair.new),
          emp: entryEmp(pair.new) || base.emp,
          oldVal: pair.old.amount,
          newVal: pair.new.amount,
        })
      );
      return; // field handled as entry diff
    }

    let type = 'edit';
    if (log.action === 'MANAGER_BANK_ADD') type = 'add';
    else if (isEmptyVal(o) && !isEmptyVal(n)) type = 'add';
    else if (!isEmptyVal(o) && isEmptyVal(n)) type = 'del';
    out.push({
      ...base,
      key: `${log.auditId}:${f}`,
      type,
      field: f,
      fieldLabel: fieldLabel(f),
      oldVal: o,
      newVal: n,
    });
  });

  return out;
};

// ── Session card — all changes within one reading session, laid out in a
//    horizontal grid of change tiles so wide screens fill edge-to-edge. ─────
class SessionCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { expanded: false };
  }

  renderVals(line) {
    if (line.isWhole) {
      return (
        <Box sx={{ color: '#0a8a4a', fontWeight: 600 }}>
          {renderValue(line.wholeValues)}
        </Box>
      );
    }
    if (line.type === 'add') {
      return (
        <Box sx={{ color: '#0a8a4a', fontWeight: 700 }}>
          + {renderValue(line.newVal)}
        </Box>
      );
    }
    if (line.type === 'del') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              color: '#c0392b',
              textDecoration: 'line-through',
              fontWeight: 700,
            }}
          >
            {renderValue(line.oldVal)}
          </Box>
          <Box component="span" sx={{ color: '#c0392b', fontWeight: 700 }}>
            Removed
          </Box>
        </Box>
      );
    }
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <Box
          sx={{
            color: '#c0392b',
            textDecoration: 'line-through',
            fontWeight: 700,
          }}
        >
          {renderValue(line.oldVal)}
        </Box>
        <Box component="span" sx={{ color: '#9aa5b8' }}>
          →
        </Box>
        <Box sx={{ color: '#0a8a4a', fontWeight: 700 }}>
          {renderValue(line.newVal)}
        </Box>
      </Box>
    );
  }

  // One change rendered as a self-contained tile (so it tiles in a grid).
  renderChange(line) {
    const { onViewShiftReport } = this.props;
    const badge = TYPE_BADGE[line.type] || TYPE_BADGE.edit;
    const rs = roleStyle(line.log.userRoleKey);
    return (
      <Box
        key={line.key}
        sx={{
          border: '1px solid #eef1f7',
          borderRadius: 2.5,
          bgcolor: '#fff',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        {/* time + badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              color: '#0b1733',
              bgcolor: '#eef2f9',
              px: 0.75,
              py: '2px',
              borderRadius: '5px',
            }}
          >
            {timeOnly(line.time)}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 9.5,
              fontWeight: 800,
              px: 0.85,
              py: '3px',
              borderRadius: '5px',
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              color: badge.color,
              bgcolor: badge.bg,
            }}
          >
            {badge.label}
          </Box>
        </Box>

        {/* who made the change */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: rs.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: 11.5, fontWeight: 700, color: '#0b1733' }}
          >
            {line.log.userName || '—'}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: 9,
              fontWeight: 800,
              px: 0.6,
              py: '2px',
              borderRadius: '4px',
              letterSpacing: '.4px',
              textTransform: 'uppercase',
              color: rs.color,
              bgcolor: rs.soft,
            }}
          >
            {line.log.userRoleLabel || 'Employee'}
          </Box>
        </Box>

        {/* field */}
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: '#0b1733',
            lineHeight: 1.3,
          }}
        >
          {line.fieldLabel}
          {line.emp && (
            <Box component="span" sx={{ color: '#6b7896', fontWeight: 600 }}>
              {' '}
              — {line.emp}
            </Box>
          )}
        </Typography>

        {/* values */}
        <Box sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {this.renderVals(line)}
        </Box>

        {line.target && onViewShiftReport && (
          <Button
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={() => onViewShiftReport(line.target)}
            sx={{
              mt: 0.25,
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontSize: 11.5,
              p: '2px 6px',
              minWidth: 0,
            }}
          >
            View shift report
          </Button>
        )}
      </Box>
    );
  }

  render() {
    const { session } = this.props;
    const { expanded } = this.state;

    return (
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #e6ebf3',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(15,23,42,.03)',
          transition: 'box-shadow .2s ease',
          '&:hover': { boxShadow: '0 8px 24px rgba(15,23,42,.07)' },
        }}
      >
        {/* header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.75,
            background: 'linear-gradient(180deg, #eef2f9 0%, #fff 100%)',
            borderBottom: '1px solid #eef1f7',
          }}
        >
          <Box
            sx={{
              minWidth: 48,
              height: 42,
              px: 1,
              borderRadius: 2,
              bgcolor: '#0b1733',
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
              fontFamily: 'monospace',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            #{session.session}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 15,
                color: '#0b1733',
                lineHeight: 1.2,
              }}
            >
              Session #{session.session}
            </Typography>
            <Typography
              sx={{
                fontSize: 11.5,
                color: '#3a4a6b',
                mt: 0.5,
                fontWeight: 600,
              }}
            >
              <b>{session.count}</b> change{session.count !== 1 ? 's' : ''}
            </Typography>
          </Box>
          {/* contributors (hidden on phones — each tile already shows its author) */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              maxWidth: '45%',
            }}
          >
            {session.users.map((u) => {
              const rs = roleStyle(u.roleKey);
              return (
                <Box
                  key={u.name || '?'}
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: rs.color,
                    bgcolor: rs.soft,
                    px: 0.85,
                    py: '3px',
                    borderRadius: '999px',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: rs.color,
                    }}
                  />
                  {u.name}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* horizontal grid of change tiles */}
        <Box
          sx={{
            p: 1.5,
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(290px, 1fr))',
            },
          }}
        >
          {session.lines.map((line) => this.renderChange(line))}
        </Box>

        {/* expandable footer */}
        <Box
          onClick={() => this.setState({ expanded: !expanded })}
          sx={{
            borderTop: '1px solid #eef1f7',
            bgcolor: '#fbfcfe',
            px: 1.75,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#6b7896',
              letterSpacing: '.6px',
            }}
          >
            Session
          </Typography>
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: '#0b1733', flex: 1 }}
          >
            #{session.session} · {session.count} change
            {session.count !== 1 ? 's' : ''}
          </Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: expanded ? '#0b1733' : '#6b7896',
            }}
          >
            {expanded ? 'Less ⌃' : 'More ⌄'}
          </Typography>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              bgcolor: '#fbfcfe',
              px: 1.75,
              pb: 1.5,
              borderTop: '1px dashed #eef1f7',
            }}
          >
            {[
              [
                'Edited by',
                session.users
                  .map((u) => `${u.name} (${u.roleLabel})`)
                  .join(', ') || '—',
              ],
              ['Total changes (this day)', session.count],
              ['First change', fullDate(session.first)],
              ['Last change', fullDate(session.last)],
            ].map(([k, v], i) => (
              <Box
                key={k}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.9,
                  borderTop: i === 0 ? 'none' : '1px dashed #eef1f7',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: '#6b7896',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {k}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: '#0b1733',
                    fontWeight: 700,
                    textAlign: 'right',
                  }}
                >
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }
}

class WeeklyReportAuditLog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      feedbackBarOpen: false,
      feedbackMessage: '',
      feedbackStatus: '',
      selectedDayKey: null,
    };
  }

  componentDidMount() {
    this.fetch();
  }

  fetch = () => {
    const payload = { locationId: this.props.userInfo.selectedLocationId };
    if (Array.isArray(this.props.sessionIds) && this.props.sessionIds.length) {
      payload.readingSessionIds = this.props.sessionIds;
    } else if (this.props.embeddedSessionId) {
      payload.readingSessionId = this.props.embeddedSessionId;
    }
    this.props.listWeeklyReportAudit(payload);
  };

  closeFeedbackBar = () => this.setState({ feedbackBarOpen: false });

  // Flatten + group logs into days → sessions → change lines.
  buildModel() {
    const logs = this.props.logs || [];
    const lines = [];
    logs.forEach((log) => buildLines(log).forEach((l) => lines.push(l)));

    const byDay = {};
    lines.forEach((l) => {
      const k = dayKeyOf(l.time);
      (byDay[k] = byDay[k] || []).push(l);
    });
    const dayKeys = Object.keys(byDay).sort().reverse();

    const selected =
      this.state.selectedDayKey && byDay[this.state.selectedDayKey]
        ? this.state.selectedDayKey
        : dayKeys[0];

    const days = dayKeys.map((k) => {
      const d = parseDayKey(k);
      return {
        key: k,
        dow: d.toLocaleDateString('en-US', { weekday: 'short' }),
        dnum: d.getDate(),
        label: d.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }),
        scope: scopeLabel(k),
        count: byDay[k].length,
      };
    });

    let sessions = [];
    if (selected && byDay[selected]) {
      const bySession = {};
      byDay[selected].forEach((l) => {
        const sid = l.session || '—';
        (bySession[sid] = bySession[sid] || []).push(l);
      });
      sessions = Object.entries(bySession)
        .map(([sid, ls]) => {
          const times = ls.map((x) => +new Date(x.time));
          // distinct contributors with their role + change count
          const um = new Map();
          ls.forEach((l) => {
            const k = l.log.userId || l.log.userName || '?';
            if (!um.has(k)) {
              um.set(k, {
                name: l.log.userName,
                roleKey: l.log.userRoleKey,
                roleLabel: l.log.userRoleLabel,
                count: 0,
              });
            }
            um.get(k).count += 1;
          });
          return {
            session: sid,
            count: ls.length,
            users: Array.from(um.values()),
            first: Math.min(...times),
            last: Math.max(...times),
            lines: ls
              .slice()
              .sort((a, b) => new Date(b.time) - new Date(a.time)),
          };
        })
        .sort((a, b) => {
          const na = parseInt(a.session, 10);
          const nb = parseInt(b.session, 10);
          if (!isNaN(na) && !isNaN(nb)) return nb - na;
          return b.last - a.last;
        });
    }

    return { days, selected, sessions, totalLines: lines.length };
  }

  render() {
    const isEmbedded = !!this.props.onBack;
    const titleSuffix = this.props.embeddedSessionId
      ? ` — Session #${this.props.embeddedSessionId}`
      : '';

    const headerButtons = [];
    if (isEmbedded) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: this.props.onBack,
        title: 'Back',
      });
    }
    headerButtons.push({
      icon: <RefreshIcon />,
      handler: this.fetch,
      title: 'Refresh',
    });

    const { days, selected, sessions, totalLines } = this.buildModel();
    const selectedDay = days.find((d) => d.key === selected);

    return (
      <React.Fragment>
        <CustomToolbar
          title={`Daily Report Audit${titleSuffix}`}
          createTitle={''}
          backTitle={''}
          headerButtons={headerButtons}
        />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <Box
          sx={{
            bgcolor: '#f5f7fb',
            minHeight: '100%',
            px: { xs: 1.5, sm: 2.5 },
            py: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 800,
                color: '#0b1733',
                letterSpacing: '-.5px',
              }}
            >
              Report Changes
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#3a4a6b' }}>
              Edits to reports — grouped by report day and the session they
              belong to.
            </Typography>
          </Box>

          {totalLines === 0 ? (
            <Box
              sx={{
                mt: 3,
                p: '50px 24px',
                textAlign: 'center',
                bgcolor: '#fff',
                border: '1px dashed #e6ebf3',
                borderRadius: 4,
              }}
            >
              <Box sx={{ fontSize: 36, opacity: 0.6, mb: 1 }}>✓</Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 700, color: '#0b1733' }}
              >
                No report changes
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7896', mt: 0.5 }}>
                Nothing has been edited for{' '}
                {titleSuffix ? 'this session' : 'these sessions'} yet.
              </Typography>
            </Box>
          ) : (
            <>
              {/* day strip */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 1,
                  mt: 1,
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {days.map((d) => {
                  const active = d.key === selected;
                  return (
                    <Box
                      key={d.key}
                      onClick={() => this.setState({ selectedDayKey: d.key })}
                      sx={{
                        flex: '0 0 auto',
                        position: 'relative',
                        cursor: 'pointer',
                        minWidth: 66,
                        textAlign: 'center',
                        borderRadius: '14px',
                        px: 1.5,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: active ? '#0b1733' : '#e6ebf3',
                        bgcolor: active ? '#0b1733' : '#fff',
                        transition: 'all .15s ease',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.8px',
                          color: active ? '#9fb0d4' : '#6b7896',
                        }}
                      >
                        {d.dow}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 800,
                          lineHeight: 1,
                          mt: 0.25,
                          color: active ? '#fff' : '#0b1733',
                        }}
                      >
                        {d.dnum}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          top: 5,
                          right: 5,
                          fontSize: 9,
                          fontWeight: 800,
                          lineHeight: 1.4,
                          borderRadius: '999px',
                          px: '5px',
                          color: active ? '#0b1733' : '#fff',
                          bgcolor: active ? '#ffd400' : '#e53935',
                        }}
                      >
                        {d.count}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* selected day header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mt: 1.5,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: '#6b7896',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}
                  >
                    {selectedDay ? selectedDay.scope : ''}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: '#0b1733',
                      letterSpacing: '-.3px',
                    }}
                  >
                    {selectedDay ? selectedDay.label : ''}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid #e6ebf3',
                    borderRadius: '999px',
                    px: 1.25,
                    py: 0.6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#3a4a6b',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <b style={{ color: '#0b1733' }}>
                    {selectedDay ? selectedDay.count : 0}
                  </b>{' '}
                  change{selectedDay && selectedDay.count !== 1 ? 's' : ''} ·{' '}
                  <b style={{ color: '#0b1733' }}>{sessions.length}</b> session
                  {sessions.length !== 1 ? 's' : ''}
                </Box>
              </Box>

              {/* full-width session cards (change tiles flow horizontally inside) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sessions.map((s) => (
                  <SessionCard
                    key={s.session}
                    session={s}
                    onViewShiftReport={this.props.onViewShiftReport}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  logs: weeklyReportAuditLogsSelector(state),
});

const actionCreators = {
  listWeeklyReportAudit,
};

export default withShipment(
  { mapStateToProps, actionCreators },
  WeeklyReportAuditLog
);
